import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { siteContent } from '../data/mock';
import { Menu, X, Globe } from 'lucide-react';

const OmLogo = ({ className }) => (
  <svg viewBox="0 0 100 100" className={className}>
    <defs>
      <linearGradient id="omGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style={{ stopColor: '#F97316' }} />
        <stop offset="100%" style={{ stopColor: '#F59E0B' }} />
      </linearGradient>
    </defs>
    <circle cx="50" cy="50" r="48" fill="url(#omGradient)" />
    <text x="50" y="68" fontFamily="Arial, sans-serif" fontSize="55" fontWeight="bold" fill="white" textAnchor="middle">ॐ</text>
  </svg>
);

const Header = () => {
  const { language, toggleLanguage } = useLanguage();
  const content = siteContent[language].nav;
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { key: 'home', href: '#home' },
    { key: 'about', href: '#about' },
    { key: 'teachings', href: '#teachings' },
    { key: 'bhajans', href: '#bhajans' },
    { key: 'ashram', href: '#ashram' },
    { key: 'gallery', href: '#gallery' },
    { key: 'contact', href: '#contact' }
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo with Om Symbol */}
          <div className="flex items-center gap-2 sm:gap-3">
            <OmLogo className="w-10 h-10 md:w-12 md:h-12 drop-shadow-lg" />
            <div className="hidden xs:block">
              <h1 className={`font-bold text-sm md:text-base ${isScrolled ? 'text-orange-800' : 'text-white'}`}>
                {language === 'gu' ? 'ઉમેદપુરી બાપુ' : 'Umedpuri Bapu'}
              </h1>
              <p className={`text-xs ${isScrolled ? 'text-orange-600' : 'text-orange-100'}`}>
                {language === 'gu' ? 'સાદુથાલા' : 'Saduthala'}
              </p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={() => scrollToSection(item.href)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:bg-orange-100/80 ${
                  isScrolled ? 'text-orange-800 hover:text-orange-600' : 'text-white hover:text-orange-200'
                }`}
              >
                {content[item.key]}
              </button>
            ))}
          </nav>

          {/* Language Toggle & Mobile Menu */}
          <div className="flex items-center gap-2">
            <button
              onClick={toggleLanguage}
              className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                isScrolled
                  ? 'bg-orange-100 text-orange-800 hover:bg-orange-200'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              <Globe className="w-4 h-4" />
              <span>{language === 'gu' ? 'EN' : 'ગુજ'}</span>
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 rounded-lg transition-colors ${
                isScrolled ? 'text-orange-800 hover:bg-orange-100' : 'text-white hover:bg-white/20'
              }`}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-white/95 backdrop-blur-md shadow-lg border-t border-orange-100">
            <nav className="flex flex-col py-4">
              {navItems.map((item) => (
                <button
                  key={item.key}
                  onClick={() => scrollToSection(item.href)}
                  className="px-6 py-3 text-left text-orange-800 hover:bg-orange-50 transition-colors"
                >
                  {content[item.key]}
                </button>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
